const SALT_SIZE = 16;
const IV_SIZE = 12;
const ALGORITHM = 'AES-GCM';
const ITERATIONS = 250000;

const enc = new TextEncoder();

const getPasswordKey = (password: string) =>
  crypto.subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, ['deriveKey']);

const deriveKey = (passwordKey: CryptoKey, salt: BufferSource, keyUsages: KeyUsage[]) =>
  crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: ITERATIONS,
      hash: 'SHA-256',
    },
    passwordKey,
    { name: ALGORITHM, length: 256 },
    false,
    keyUsages,
  );

const buffToBase64 = (buff: Uint8Array) =>
  btoa(
    buff.reduce((data, byte) => {
      return data + String.fromCharCode(byte);
    }, ''),
  );

export async function encrypt(secretData: string, password: string) {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_SIZE));
  const iv = crypto.getRandomValues(new Uint8Array(IV_SIZE));
  const passwordKey = await getPasswordKey(password);
  const aesKey = await deriveKey(passwordKey, salt, ['encrypt']);

  const encryptedContent = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv },
    aesKey,
    new TextEncoder().encode(secretData),
  );

  const encryptedContentArr = new Uint8Array(encryptedContent);

  const buff = new Uint8Array(salt.byteLength + iv.byteLength + encryptedContentArr.byteLength);
  buff.set(salt, 0);
  buff.set(iv, salt.byteLength);
  buff.set(encryptedContentArr, salt.byteLength + iv.byteLength);

  return buffToBase64(buff);
}

const base64ToBuf = (base64: string) => {
  const str = atob(base64);
  const buff = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) {
    buff[i] = str.charCodeAt(i);
  }
  return buff;
};

export async function decrypt(encryptedData: string, password: string) {
  const encryptedDataBuff = base64ToBuf(encryptedData);

  const salt = encryptedDataBuff.slice(0, SALT_SIZE);
  const iv = encryptedDataBuff.slice(SALT_SIZE, SALT_SIZE + IV_SIZE);
  const data = encryptedDataBuff.slice(SALT_SIZE + IV_SIZE);

  const passwordKey = await getPasswordKey(password);
  const aesKey = await deriveKey(passwordKey, salt, ['decrypt']);
  const decryptedContent = await crypto.subtle.decrypt({ name: ALGORITHM, iv }, aesKey, data);

  return new TextDecoder().decode(decryptedContent);
}
